import { useEffect, useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Box, Button, Flex, HStack, IconButton, Link, NativeSelect, Stack, Text } from '@chakra-ui/react'
import { LuTrash } from 'react-icons/lu'
import { UserSection } from './UserSection'
import { setEventRole } from '../../services/userAdmin'
import { fetchEvents } from '../../services/firebaseService'
import { EVENT_ROLES } from '../../constants/eventRoles'
import { useTranslation } from '../../i18n'

function RoleSelect({ value, onChange, disabled, label }) {
    const { t } = useTranslation()
    return (
        <NativeSelect.Root size="sm" width={{ base: '100%', sm: '220px' }} disabled={disabled}>
            <NativeSelect.Field aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>
                {EVENT_ROLES.map((r) => <option key={r} value={r}>{t(`users.roles.${r}`)}</option>)}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
        </NativeSelect.Root>
    )
}

export function EventRolesCard({ user, act }) {
    const { t } = useTranslation()
    const [events, setEvents] = useState(null)
    const [busy, setBusy] = useState(null)
    const [newEventId, setNewEventId] = useState('')
    const [newRole, setNewRole] = useState('moderator')

    useEffect(() => {
        fetchEvents({ isSysAdmin: true }).then(setEvents).catch(() => setEvents([]))
    }, [])

    const assigned = useMemo(() => new Set(user.eventRoles.map((r) => r.eventId)), [user.eventRoles])
    const available = (events || []).filter((e) => !assigned.has(e.id))

    const run = async (key, fn, success) => {
        setBusy(key)
        const ok = await act(fn, success)
        setBusy(null)
        return ok
    }

    const add = async () => {
        const ok = await run('add', () => setEventRole({ uid: user.uid, eventId: newEventId, role: newRole }),
            t('users.events.added'))
        if (ok) setNewEventId('')
    }

    return (
        <UserSection id="user-events" title={t('users.events.title')}>
            {user.eventRoles.length === 0 ? (
                <Text fontSize="sm" color="var(--mc-text-muted)">{t('users.events.none')}</Text>
            ) : (
                <Stack gap={2} as="ul" listStyleType="none" m={0}>
                    {user.eventRoles.map((r) => (
                        <Flex as="li" key={r.eventId} gap={3} align="center" wrap="wrap">
                            <Box flex="1" minW="160px">
                                {r.eventName != null ? (
                                    <Link asChild color="var(--mc-text)" fontWeight="500">
                                        <RouterLink to={`/events/${r.eventId}`}>{r.eventName || t('users.events.untitled')}</RouterLink>
                                    </Link>
                                ) : (
                                    <Text color="var(--mc-text-muted)">
                                        {t('users.events.missingEvent')} · <Text as="span" fontFamily="'JetBrains Mono', monospace" fontSize="xs">{r.eventId}</Text>
                                    </Text>
                                )}
                            </Box>
                            <RoleSelect
                                label={t('users.events.role')}
                                value={r.role}
                                disabled={busy === r.eventId}
                                onChange={(role) => run(r.eventId,
                                    () => setEventRole({ uid: user.uid, eventId: r.eventId, role }),
                                    t('users.events.changed'))}
                            />
                            <IconButton
                                size="sm"
                                variant="ghost"
                                colorPalette="red"
                                aria-label={t('users.events.remove')}
                                title={t('users.events.remove')}
                                loading={busy === `rm-${r.eventId}`}
                                onClick={() => run(`rm-${r.eventId}`,
                                    () => setEventRole({ uid: user.uid, eventId: r.eventId, role: null }),
                                    t('users.events.removed'))}
                            >
                                <LuTrash />
                            </IconButton>
                        </Flex>
                    ))}
                </Stack>
            )}

            <HStack gap={2} wrap="wrap" pt={2} borderTop="1px solid" borderColor="var(--mc-border)">
                <NativeSelect.Root size="sm" width={{ base: '100%', sm: '280px' }} disabled={!events}>
                    <NativeSelect.Field
                        aria-label={t('users.events.pickEvent')}
                        value={newEventId}
                        onChange={(e) => setNewEventId(e.target.value)}
                    >
                        <option value="">{t('users.events.pickEvent')}</option>
                        {available.map((e) => (
                            <option key={e.id} value={e.id}>{e.name || t('users.events.untitled')}</option>
                        ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                </NativeSelect.Root>
                <RoleSelect label={t('users.events.role')} value={newRole} onChange={setNewRole} />
                <Button size="sm" variant="outline" onClick={add} disabled={!newEventId} loading={busy === 'add'}>
                    {t('users.events.add')}
                </Button>
            </HStack>
        </UserSection>
    )
}
